import type { ActualizeSkips, ForecastCompareResponse } from "../api/forecast_api"

export interface ActualsPreviewMessage {
  title: string
  description: string
  /** False when no actual landed: there is nothing to review and nothing to save. */
  has_actuals: boolean
}

function plural(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`
}

/** Why invoice lines stayed off this version, in the words the Mappings page uses. */
export function skip_sentence(skips: ActualizeSkips | null | undefined): string | null {
  if (!skips) return null
  const parts = [
    skips.no_sku_node_lines ? `${plural(skips.no_sku_node_lines, "line")} for a customer and product this version has no row for` : null,
    skips.unmapped_customer_lines ? `${plural(skips.unmapped_customer_lines, "line")} from an unmapped customer` : null,
    skips.unmapped_product_lines ? `${plural(skips.unmapped_product_lines, "line")} with an unmapped product` : null,
  ].filter(Boolean)
  return parts.length > 0 ? `Not placed: ${parts.join(", ")}.` : null
}

/**
 * The sync's result as the planner should read it. The count that matters is actuals landing in
 * this plan's months: the response's cells also hold every plan cell, which is how a test version
 * with 6 SKUs over 12 months announced "Loaded 72 compare cells" and showed nothing.
 */
export function actuals_preview_message(res: ForecastCompareResponse, provider_label: string): ActualsPreviewMessage {
  const actual_cells = res.actual_cells ?? 0
  const skips = skip_sentence(res.skips)
  if (actual_cells === 0) {
    return {
      title: `No ${provider_label} actuals for this version`,
      description: [`None of its ${plural(res.plan_cells ?? 0, "plan cell")} matched an invoice line.`, skips].filter(Boolean).join(" "),
      has_actuals: false,
    }
  }
  return {
    title: `${provider_label} actuals preview ready`,
    description: [`${plural(actual_cells, "month")} of actuals across this plan's rows. Review before saving.`, skips]
      .filter(Boolean)
      .join(" "),
    has_actuals: true,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function response(fields: Partial<ForecastCompareResponse>): ForecastCompareResponse {
    return { version_id: "v", cells: [], ...fields }
  }

  describe("actuals_preview_message", () => {
    it("does not call plan cells loaded actuals", () => {
      const message = actuals_preview_message(
        response({
          plan_cells: 72,
          actual_cells: 0,
          skips: { unmapped_product_lines: 0, unmapped_customer_lines: 14, no_sku_node_lines: 24000 },
        }),
        "NetSuite",
      )
      expect(message.has_actuals).toBe(false)
      expect(message.title).toBe("No NetSuite actuals for this version")
      expect(message.description).toContain("None of its 72 plan cells")
      expect(message.description).toContain("24,000 lines for a customer and product this version has no row for")
    })

    it("counts the months that landed", () => {
      const message = actuals_preview_message(response({ plan_cells: 10932, actual_cells: 1906 }), "NetSuite")
      expect(message.has_actuals).toBe(true)
      expect(message.description).toBe("1,906 months of actuals across this plan's rows. Review before saving.")
    })
  })
}
